import React from 'react';

import { Avatar, Button, Stack, Text, Title, useMantineTheme } from '@mantine/core';
import { useGoogleLogin } from '@react-oauth/google';
import { IconCalendarEvent } from '@tabler/icons-react';
import KiloAlert from 'components/KiloAlert';
import useCreateGoogleCalendarIntegration from 'hooks/googleCalendarIntegration/useCreateGoogleCalendarIntegration';
import notificationHandler from 'utils/notifications';

import { GOOGLE_API_SCOPES } from '../Integration';

const CreateGoogleCalendarIntegrationState = (): JSX.Element => {
  const theme = useMantineTheme();

  const { successNotification, errorNotification } = notificationHandler();
  const createGoogleCalendarIntegrationMutation = useCreateGoogleCalendarIntegration();

  const handleGoogleCalendarIntegrationCreate = (authorizationCode: string): void => {
    createGoogleCalendarIntegrationMutation.mutate(
      { authorizationCode: authorizationCode },
      {
        onError: () => {
          errorNotification(
            'Integration creation failed',
            <Text
              component='a'
              href='https://help.usekilo.com/kb/articles/connect-google-calendar-to-gms'
              target='_blank'
              rel='noopener noreferrer'
              style={{ color: theme.colors.kiloBlue[7], textDecoration: 'underline' }}
            >
              Refer to this document for more detailed instructions.
            </Text>,
          );
        },
        onSuccess: () => {
          successNotification('Integration created successfully');
        },
      },
    );
  };

  const login = useGoogleLogin({
    onSuccess: response => handleGoogleCalendarIntegrationCreate(response.code),
    flow: 'auth-code',
    scope: GOOGLE_API_SCOPES.join(' '),
  });

  return (
    <Stack p='xl' spacing='md' align='center'>
      <Avatar size='xl' radius='100%'>
        <IconCalendarEvent />
      </Avatar>

      <Title order={2}>Google Calendar Integration</Title>

      <Text color='dimmed'>Allow Kilo to integrate with your Google Calendar</Text>

      <KiloAlert mt='md'>
        <Stack spacing={'xs'}>
          <Text>Google will ask that you allow the Kilo system to have access to your calendar.</Text>
          <Text>
            Click the checkbox that says <strong>Select All</strong>.
          </Text>
          <Text>
            You can also{' '}
            <Text
              component='a'
              href='https://help.usekilo.com/kb/articles/connect-google-calendar-to-gms'
              target='_blank'
              rel='noopener noreferrer'
              style={{ color: theme.colors.kiloBlue[7], textDecoration: 'underline' }}
            >
              review this document
            </Text>{' '}
            for more detailed instructions.
          </Text>
        </Stack>
      </KiloAlert>

      <Button mt='md' size='md' onClick={login}>
        Connect Google Calendar
      </Button>
    </Stack>
  );
};

export default CreateGoogleCalendarIntegrationState;
